/**
 * Sign-In With Ethereum (SIWE) authentication — client-side flow.
 *
 * Flow for returning users:
 *   1. signIn(address, chainId)  → AuthSession
 *
 * Flow for new users:
 *   1. signIn(address, chainId)  → NeedsRegistration
 *   2. register(profileData)     → AuthSession  (no second wallet prompt)
 */
import { SiweMessage } from 'siwe'
import { walletService } from '@/lib/wallet/walletService'
import type { AuthSession, BuyerProfileData, NeedsRegistration } from '@/types'

class SiweAuth {
  /**
   * Full SIWE sign-in flow: fetches nonce → builds message → signs → verifies.
   *
   * Returns `AuthSession` for registered wallets.
   * Returns `NeedsRegistration` for wallets without an account — the caller
   * should present ProfileForm and call register() next.
   */
  async signIn(address: string, chainId: number): Promise<AuthSession | NeedsRegistration> {
    // 1. Fetch nonce (stored in session cookie by the server)
    const nonceRes = await fetch('/api/auth/nonce', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    })
    if (!nonceRes.ok) {
      throw new Error(`Failed to get nonce (${nonceRes.status})`)
    }
    const { nonce } = (await nonceRes.json()) as { nonce: string }

    // 2. Build EIP-4361 message
    // NOTE: statement must be printable ASCII only (SIWE v3 ABNF parser rejects
    // non-ASCII characters such as the em dash U+2014).
    const message = new SiweMessage({
      domain: window.location.host,
      address,
      statement: 'Sign in to SaasFarm - the blockchain development marketplace.',
      uri: window.location.origin,
      version: '1',
      chainId,
      nonce,
      issuedAt: new Date().toISOString(),
      expirationTime: new Date(Date.now() + 10 * 60 * 1000).toISOString(), // 10-min window
    })
    const messageStr = message.prepareMessage()

    // 3. Sign with native wallet (no WalletConnect)
    const signature = await walletService.signMessage(messageStr)

    // 4. Verify on server
    const verifyRes = await fetch('/api/auth/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ message: messageStr, signature }),
    })
    if (!verifyRes.ok) {
      let errMsg = 'Verification failed'
      try {
        const errBody = await verifyRes.json() as { error?: { message?: string } }
        errMsg = errBody.error?.message ?? errMsg
      } catch { /* non-JSON error response */ }
      throw new Error(errMsg)
    }

    return verifyRes.json() as Promise<AuthSession | NeedsRegistration>
  }

  /**
   * Complete registration for new users.
   * Relies on the verified address stored in the session by /verify —
   * no second wallet signature required.
   */
  async register(profileData: BuyerProfileData): Promise<AuthSession> {
    const res = await fetch('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(profileData),
    })
    if (!res.ok) {
      let errMsg = 'Registration failed'
      try {
        const err = (await res.json()) as { error?: { message?: string } }
        errMsg = err.error?.message ?? errMsg
      } catch { /* non-JSON error response */ }
      throw new Error(errMsg)
    }
    return res.json() as Promise<AuthSession>
  }

  /** Returns the current session or null if not authenticated. */
  async getSession(): Promise<AuthSession | null> {
    try {
      const res = await fetch('/api/auth/session', { credentials: 'include' })
      if (!res.ok) return null
      return res.json() as Promise<AuthSession>
    } catch {
      return null
    }
  }

  async signOut(): Promise<void> {
    await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
    })
  }
}

export const siweAuth = new SiweAuth()
